/**
 * Tile id generation — produces valid ids per the TILE_ID_PATTERN.
 *
 * Pattern: /^tile_[a-z0-9]{6,12}$/
 * Strategy: base-36 timestamp suffix + random salt, lower-cased, trimmed to 8 chars.
 * Probability of collision in a 40-tile board is astronomically low.
 *
 * Pure: no side-effects beyond reading Date.now() and Math.random().
 */
import { TILE_ID_PATTERN } from 'briefpane/schema';

/**
 * Generate a unique tile id that matches TILE_ID_PATTERN.
 * Does NOT guarantee global uniqueness — callers must check against the board
 * (board-ops.addTile returns 'id-collision' if there is one, per its contract).
 */
export function generateTileId(): string {
  // 8-char base-36 timestamp + 4-char random suffix = 12 chars total (fits 6..12)
  const ts = Date.now().toString(36).slice(-8).padStart(8, '0');
  const rand = Math.random().toString(36).slice(2, 6).padStart(4, '0');
  const id = `tile_${ts}${rand}`;
  // Sanity-check against the schema pattern (compile-time contract)
  if (!TILE_ID_PATTERN.test(id)) {
    // Fallback: guaranteed valid via a deterministic hex slice
    const fallback = `tile_${Date.now().toString(16).slice(-6)}${Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0').slice(0, 6)}`;
    return fallback;
  }
  return id;
}
